import { useCallback, useEffect, useRef, useState } from 'react';

import type { PanelUpdatePayload } from '@shiftagent/shared';

import { useConversationMessages, type ChatMessage } from '../../hooks/use-conversation-messages';
import { useConversationSkills } from '../../hooks/use-conversation-skills';
import { useSlashCommands } from '../../hooks/use-slash-commands';
import { AgentPanelsContext, useAgentPanels } from '../../hooks/use-agent-panels';

import { AgentPanelDock, AgentPanelToggle } from './AgentPanelDock';
import { Avatar, colorForSeed, initialsFor } from './Avatar';
import { Composer, type ComposerHandle } from './Composer';
import { ConversationEmptyState } from './ConversationEmptyState';
import type { ConversationListItemData } from '../../hooks/use-conversations';
import { MessageBubble } from './MessageBubble';
import { StatusPill } from './StatusPill';
import { TextStreamRenderer } from './TextStreamRenderer';
import { ThinkingCap } from './ThinkingCap';
import { deriveStatus, derivedAgentName } from './ConversationListItem';
import { shortRelativeTime } from './format-time';

/**
 * Infer what the agent is doing RIGHT NOW from its most recent stream event.
 * - `typing` → the latest event is a text delta (user sees characters appearing)
 * - `thinking` → the latest event is a tool_use / tool_result / nothing-yet
 *                (user sees the rotating ThinkingCap phrases instead)
 */
function inferStreamMode(events: Record<string, unknown>[] | undefined): 'typing' | 'thinking' {
  if (!events || events.length === 0) return 'thinking';
  const last = events[events.length - 1];
  return last?.type === 'content_block_delta' ? 'typing' : 'thinking';
}

function latestToolName(events: Record<string, unknown>[] | undefined): string | undefined {
  if (!events) return undefined;
  for (let i = events.length - 1; i >= 0; i--) {
    const evt = events[i];
    if (evt?.type === 'content_block_start') {
      const name = evt.tool_name;
      if (typeof name === 'string') return name;
    }
  }
  return undefined;
}

interface Props {
  conversation: ConversationListItemData;
  onPopOut?: (() => void) | undefined;
  onOpenLinkedTask?: (() => void) | undefined;
  /**
   * Called after a chat stream completes (or the agent finishes via the
   * extended-session poll). The parent uses this to refresh the conversation's
   * row in the list — so its `processing_status` flips from 'processing' back
   * to 'idle' and the "Working" status pill in the rail clears.
   */
  onStreamComplete?: ((conversationId: string) => void) | undefined;
  /**
   * Flip Creator Mode on/off for this conversation. Must come from the parent
   * that owns the `conversations` list state — calling `useConversations()` in
   * a child component creates an independent hook instance whose optimistic
   * updates never reach the rendered list. The parent should pass through its
   * own `useConversations().setCreatorMode`.
   */
  onSetCreatorMode?: ((conversationId: string, enabled: boolean) => Promise<void> | void) | undefined;
  /**
   * Rename the conversation. Like `onSetCreatorMode`, this must come from the
   * parent that owns the `conversations` list state so the optimistic title +
   * lock update reaches the rendered list. A manual rename permanently locks the
   * title against LLM auto-titling.
   */
  onRename?: ((conversationId: string, title: string) => Promise<void> | void) | undefined;
}

/**
 * Scroll distance (px) from the bottom beyond which the viewport is considered
 * "scrolled up". Used for the two scroll behaviors:
 *   1. Suppress auto-scroll-to-bottom when the user has deliberately scrolled up.
 *   2. Show the "New messages" pill when new content arrives off-screen.
 */
const SCROLL_STICKY_THRESHOLD = 120;

export function ConversationThread({ conversation, onPopOut, onOpenLinkedTask, onStreamComplete, onSetCreatorMode, onRename }: Props) {
  const { messages, status, isAgentTyping, isExtendedSession, sendMessage, retryLastMessage } =
    useConversationMessages(conversation.id, { onStreamComplete });
  const slash = useSlashCommands();
  const skills = useConversationSkills(conversation.id);
  const panels = useAgentPanels();
  const scrollRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<ComposerHandle>(null);
  const isUserScrolledUp = useRef(false);
  const [showNewMessages, setShowNewMessages] = useState(false);

  // Inline title edit. `editingTitle` is the in-progress buffer; null = not editing.
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const beginTitleEdit = useCallback(() => {
    if (!onRename) return;
    setEditingTitle(conversation.title);
    requestAnimationFrame(() => {
      const el = titleInputRef.current;
      if (el) {
        el.focus();
        el.select();
      }
    });
  }, [onRename, conversation.title]);

  const commitTitleEdit = useCallback(() => {
    setEditingTitle((current) => {
      if (current === null) return null;
      const next = current.trim();
      // Only persist a real, changed title; otherwise just exit edit mode.
      if (next && next !== conversation.title && onRename) {
        void onRename(conversation.id, next);
      }
      return null;
    });
  }, [conversation.id, conversation.title, onRename]);

  const cancelTitleEdit = useCallback(() => setEditingTitle(null), []);

  useEffect(() => {
    void panels.fetchAgentPanels(conversation.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.id]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_STICKY_THRESHOLD;
    isUserScrolledUp.current = !atBottom;
    if (atBottom) setShowNewMessages(false);
  }, []);

  // Auto-scroll: fires on every messages change, but only if the user hasn't
  // deliberately scrolled up. This keeps streaming growth visible without
  // yanking the viewport while the user is reading older context.
  useEffect(() => {
    if (isUserScrolledUp.current) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  // "New messages" pill: only when a *new* message arrives while the user is
  // scrolled up. Depends on `messages.length` (not ref identity) so growth of
  // a streaming message doesn't re-trigger the pill.
  useEffect(() => {
    if (isUserScrolledUp.current && messages.length > 0) {
      setShowNewMessages(true);
    }
  }, [messages.length]);

  const handlePlanApproved = useCallback(
    (planId: string) => {
      void sendMessage(`[SYSTEM_TRIGGER:plan_approved:${planId}]`);
    },
    [sendMessage],
  );

  const handleJumpToLatest = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    setShowNewMessages(false);
  }, []);

  const agent = derivedAgentName(conversation.title);
  const agentColor = colorForSeed(agent);
  const convStatus = deriveStatus(conversation);
  const time = shortRelativeTime(conversation.updated_at);

  // Derive live stream mode from the in-flight assistant message.
  const streamingMsg = messages.find((m) => m.id === 'asst_streaming');
  const streamMode = inferStreamMode(streamingMsg?.streamEvents);
  const currentTool = latestToolName(streamingMsg?.streamEvents);

  return (
    <AgentPanelsContext.Provider value={panels}>
      <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            minHeight: 0,
            position: 'relative',
          }}
        >
          <div
            style={{
              padding: '14px 20px',
              borderBottom: '1px solid var(--border)',
              background: 'var(--bg)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexShrink: 0,
            }}
          >
            <Avatar initials={initialsFor(agent)} color={agentColor} size={28} />
            <div style={{ flex: 1, minWidth: 0 }}>
              {editingTitle !== null ? (
                <input
                  ref={titleInputRef}
                  value={editingTitle}
                  onChange={(e) => setEditingTitle(e.target.value)}
                  onBlur={commitTitleEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitTitleEdit();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelTitleEdit();
                    }
                  }}
                  aria-label="Conversation title"
                  style={{
                    width: '100%',
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--text)',
                    background: 'var(--surface)',
                    border: '1px solid var(--accent)',
                    borderRadius: 5,
                    padding: '2px 6px',
                    outline: 'none',
                    fontFamily: 'inherit',
                  }}
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: 'var(--text)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {conversation.title}
                  </div>
                  {onRename && (
                    <button
                      type="button"
                      onClick={beginTitleEdit}
                      title="Rename conversation"
                      aria-label="Rename conversation"
                      style={{
                        flexShrink: 0,
                        width: 20,
                        height: 20,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 0,
                        background: 'transparent',
                        color: 'var(--text-mute)',
                        cursor: 'pointer',
                        fontSize: 11,
                        padding: 0,
                        borderRadius: 4,
                        lineHeight: 1,
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent)')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-mute)')}
                    >
                      ✎
                    </button>
                  )}
                </div>
              )}
              <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
                {agent}
                {time && ` · ${time}`}
              </div>
            </div>
            <StatusPill status={convStatus} />
            {/*
              Creator Mode toggle. When ON, this conversation's claims schedule
              against a RW-repo warm-pool so the agent can author skills,
              sub-agents, and slash commands; writes commit back to git on
              session end. When OFF, the repo is read-only (chat default).
              The new posture applies on the NEXT message — Creator Mode is
              read at claim-create time.
            */}
            <button
              type="button"
              onClick={() => {
                if (!onSetCreatorMode) return;
                void onSetCreatorMode(conversation.id, !conversation.creator_mode);
              }}
              disabled={!onSetCreatorMode}
              title={
                conversation.creator_mode
                  ? 'Creator Mode is ON — agent can author skills, sub-agents, and slash commands; writes commit back to git. Click to disable.'
                  : 'Creator Mode is OFF — agent is in chat mode (repo read-only). Click to enable authoring of skills, sub-agents, and slash commands.'
              }
              style={{
                padding: '5px 10px',
                fontSize: 11,
                fontWeight: 500,
                border: `1px solid ${conversation.creator_mode ? 'var(--accent)' : 'var(--border-strong)'}`,
                borderRadius: 6,
                background: conversation.creator_mode ? 'var(--accent)' : 'var(--surface)',
                color: conversation.creator_mode ? 'white' : 'var(--text-soft)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <span aria-hidden="true">{conversation.creator_mode ? '✎' : '✎'}</span>
              <span>Creator Mode {conversation.creator_mode ? 'ON' : 'OFF'}</span>
            </button>
            {onOpenLinkedTask && (
              <button
                type="button"
                onClick={onOpenLinkedTask}
                style={{
                  padding: '5px 10px',
                  fontSize: 11,
                  border: '1px solid var(--border-strong)',
                  borderRadius: 6,
                  background: 'var(--surface)',
                  cursor: 'pointer',
                  color: 'var(--accent)',
                }}
              >
                Open linked task →
              </button>
            )}
            <AgentPanelToggle hook={panels} />
            {onPopOut && (
              <button
                type="button"
                onClick={onPopOut}
                title="Open in new tab"
                style={{
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  background: 'var(--surface)',
                  cursor: 'pointer',
                  color: 'var(--text-soft)',
                }}
              >
                ↗
              </button>
            )}
          </div>

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            style={{
              flex: 1,
              minWidth: 0,
              overflowY: 'auto',
              // Clip horizontally so long/unbreakable message content (code,
              // URLs, wide markdown) wraps inside the pane instead of pushing
              // the whole detail view past the frame. DESIGN_DEVIATION: layout
              // — message content must shrink with the resizable detail pane.
              overflowX: 'hidden',
              padding: '18px 20px',
              background: 'var(--bg)',
              position: 'relative',
            }}
          >
            {status === 'loading' && messages.length === 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'var(--text-mute)',
                  fontSize: 12,
                }}
              >
                Loading…
              </div>
            )}

            {status !== 'loading' && messages.length === 0 && (
              <ConversationEmptyState
                availableSkills={skills.available}
                skillsStatus={skills.status}
                selectedSkillIds={skills.selected}
                onToggleSkill={skills.toggle}
                onSelectAllSkills={skills.selectAll}
                onSelectNoneSkills={skills.selectNone}
                onSaveSkills={skills.save}
                onPickSuggestion={(text) => composerRef.current?.prefill(text)}
              />
            )}

            <SectionedMessages
              messages={messages}
              onPanelUpdate={panels.updateTab}
              onPlanApproved={handlePlanApproved}
              onRetry={retryLastMessage}
            />

            {isAgentTyping && !isExtendedSession && streamMode === 'typing' && (
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--text-mute)',
                  fontStyle: 'italic',
                  padding: '4px 0',
                }}
              >
                Agent is typing…
              </div>
            )}
            {isAgentTyping && !isExtendedSession && streamMode === 'thinking' && (
              <div style={{ padding: '4px 0' }}>
                <ThinkingCap active currentTool={currentTool} />
              </div>
            )}
            {isExtendedSession && (
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--accent)',
                  padding: '6px 10px',
                  background: 'var(--accent-softer)',
                  border: '1px solid var(--accent-soft)',
                  borderRadius: 6,
                  display: 'inline-block',
                  marginTop: 6,
                }}
              >
                Extended session — the agent is still working. Messages will appear as they complete.
              </div>
            )}
          </div>

          {showNewMessages && (
            <button
              type="button"
              onClick={handleJumpToLatest}
              style={{
                position: 'absolute',
                bottom: 92,
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '6px 14px',
                fontSize: 12,
                fontWeight: 500,
                color: '#fff',
                background: 'var(--accent)',
                border: '1px solid var(--accent)',
                borderRadius: 999,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                fontFamily: 'inherit',
                zIndex: 10,
                animation: 'sd-fade-in 160ms ease-out',
              }}
            >
              ↓ New messages
            </button>
          )}

          <Composer
            ref={composerRef}
            busy={isAgentTyping}
            onSend={(text, files) => sendMessage(text, files)}
            slashCommands={slash.commands}
            slashStatus={slash.status}
            onOpenSlashPicker={slash.fetch}
            onRefreshSlashCommands={slash.fetch}
            availableSkills={skills.available}
            skillsStatus={skills.status}
            selectedSkillIds={skills.selected}
            onToggleSkill={skills.toggle}
            onSelectAllSkills={skills.selectAll}
            onSelectNoneSkills={skills.selectNone}
            onResetSkills={skills.reset}
            onSaveSkills={skills.save}
          />
        </div>
        <AgentPanelDock hook={panels} />
      </div>
    </AgentPanelsContext.Provider>
  );
}

interface SectionsProps {
  messages: ChatMessage[];
  onPanelUpdate: (payload: PanelUpdatePayload) => void;
  onPlanApproved: (planId: string) => void;
  onRetry: () => void | Promise<void>;
}

/**
 * Group messages into sections anchored on each user message, so that user
 * bubbles can be pinned via `position: sticky` while assistant replies scroll
 * underneath. Each section's z-index increases so a newer section's sticky
 * header pushes the older one up rather than stacking on top of it.
 */
function SectionedMessages({ messages, onPanelUpdate, onPlanApproved, onRetry }: SectionsProps) {
  type Section = { userMsg: ChatMessage; following: ChatMessage[] };
  const sections: Section[] = [];
  const leading: ChatMessage[] = [];

  for (const m of messages) {
    if (m.role === 'user') {
      sections.push({ userMsg: m, following: [] });
    } else if (sections.length === 0) {
      leading.push(m);
    } else {
      sections[sections.length - 1]!.following.push(m);
    }
  }

  const renderMessage = (m: ChatMessage) => {
    if (m.streamEvents && m.streamEvents.length > 0) {
      return (
        <TextStreamRenderer
          streamEvents={m.streamEvents}
          timestamp={m.timestamp}
          onPanelUpdate={onPanelUpdate}
          onPlanApproved={onPlanApproved}
        />
      );
    }
    if (!m.content) return null;
    const role = m.role === 'user' ? 'user' : 'assistant';
    return (
      <div>
        <MessageBubble role={role} content={m.content} timestamp={m.timestamp} />
        {m.status === 'error' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              justifyContent: role === 'user' ? 'flex-end' : 'flex-start',
              fontSize: 11,
              color: 'var(--kind-failure)',
              marginTop: -4,
              marginBottom: 10,
            }}
          >
            {m.error && <span>{m.error}</span>}
            <button
              type="button"
              onClick={() => {
                void onRetry();
              }}
              style={{
                fontSize: 11,
                padding: '2px 10px',
                border: '1px solid var(--border)',
                borderRadius: 6,
                background: 'var(--surface)',
                color: 'var(--accent)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Retry
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {leading.map((m) => (
        <div key={m.id}>{renderMessage(m)}</div>
      ))}
      {sections.map((section, sectionIndex) => (
        <div
          key={section.userMsg.id}
          style={{
            position: 'relative',
            zIndex: 10 + sectionIndex,
            marginBottom: sectionIndex === sections.length - 1 ? 0 : 4,
          }}
        >
          <div
            style={{
              position: 'sticky',
              top: -12,
              background: 'var(--bg)',
              paddingTop: 12,
              marginTop: -12,
              zIndex: 1,
            }}
          >
            {renderMessage(section.userMsg)}
          </div>
          {section.following.map((m) => (
            <div key={m.id} style={{ position: 'relative', zIndex: 0 }}>
              {renderMessage(m)}
            </div>
          ))}
        </div>
      ))}
    </>
  );
}
